import { useState, type ReactNode } from 'react';
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { useStore } from '@nanostores/react';
import {
  Button,
  Form,
  Heading,
  Stack,
  TextField,
  Tooltip,
  useModals,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import { VERIFICATION_REJECTED_TOOLTIP } from '@/services/billing/messages';
import { usePauseSafePermission } from '@/features/billing/hooks/use-pause-safe-permission';
import { useUpdateAccountAddress } from '@/services/db/account/mutations';
import { useAccount } from '@/services/db/account/queries';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import { readInputValue } from '@/utils/dom';
import { useBillingStripePromise } from '../hooks/use-billing-stripe';
import {
  addressFromAccount,
  applyAddressChange,
  persistAccountAddress,
  visibleBillingErrors,
} from '../utils/address-form-helpers';
import { BillingAddressFields } from './billing-address-fields';
import { CardFields } from './card-fields';
import { validateBillingAddress } from '../utils/address-validation';
import {
  useCreditCardFormSubmit,
  type CreditCardFormAddress,
  type CreditCardSubmitResult,
} from '../utils/credit-card-form-submit';
import {
  billingFieldErrors,
  firstBillingError,
  isSubmitDisabled,
  submitTooltipMessage,
} from '../utils/credit-card-form-validation';
import { UpdateAddressModal } from './update-address-modal';
import {
  CheckoutCouponBanner,
  CheckoutCouponSection,
  type CheckoutFormOptions,
  FormFooter,
} from './checkout-form-extras';

export type { CheckoutFormOptions } from './checkout-form-extras';

type CreditCardFormProps = {
  /** Submit button label (e.g. "Update payment information" / "Pay now"). */
  submitButtonText: string;
  /** Receives the confirmed SetupIntent once the card is processed and the address saved. */
  onSubmit: (result: CreditCardSubmitResult) => Promise<void>;
  /** Checkout-only extras. Omitted (the whole object) on the payment-methods update flow. */
  checkout?: CheckoutFormOptions;
  /**
   * A lower-emphasis action for the footer, left of the submit — e.g. the Cancel that closes the
   * form when it is rendered inline rather than in a dialog. Ignored in the checkout layout, which
   * has its own "Back to plans".
   */
  secondaryAction?: ReactNode;
};

/**
 * The credit-card entry form used to add or update the account's saved card. Wraps the fields in a
 * Stripe `<Elements>` provider (a null instance in test mode). Validates and saves the billing
 * address on submit, then hands the confirmed SetupIntent to `onSubmit`.
 */
export function CreditCardForm({ submitButtonText, onSubmit, checkout, secondaryAction }: CreditCardFormProps) {
  const stripe = useBillingStripePromise();

  return (
    <Elements stripe={stripe}>
      <CreditCardFormFields
        submitButtonText={submitButtonText}
        onSubmit={onSubmit}
        checkout={checkout}
        secondaryAction={secondaryAction}
      />
    </Elements>
  );
}

// Bundles the account/session data the form reads so the component keeps a small hook surface.
function useCreditCardAccountData() {
  const { account } = useAuth();
  const canEdit = usePauseSafePermission('account/billing/edit');
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const isVerificationRejected = useStore(billing.isVerificationRejected);
  const masqReadOnly = useStore(billing.isMasqReadOnly);
  const { mutateAsync: updateAccountAddress } = useUpdateAccountAddress({ accountId: account.id });
  const { data: dbAccount } = useAccount(account.id);
  // The invoice email lives on the fuller account record; fall back to the owner email, matching the
  // account settings page.
  const invoiceEmail = dbAccount != null && dbAccount.invoice_email !== '' ? dbAccount.invoice_email : account.email;

  return {
    account,
    canEdit,
    isOrdwayCustomer,
    isVerificationRejected,
    masqReadOnly,
    invoiceEmail,
    updateAccountAddress,
  };
}

function CreditCardFormFields({ submitButtonText, onSubmit, checkout, secondaryAction }: CreditCardFormProps) {
  const {
    account,
    canEdit,
    isOrdwayCustomer,
    isVerificationRejected,
    masqReadOnly,
    invoiceEmail,
    updateAccountAddress,
  } = useCreditCardAccountData();
  const snackbars = useSnackbars();
  const stripe = useStripe();
  const elements = useElements();
  const modals = useModals();

  const [name, setName] = useState('');
  const [address, setAddress] = useState<CreditCardFormAddress>(() => addressFromAccount(account));
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());

  const fieldErrors = billingFieldErrors(name, address);
  const hasRequiredFields = firstBillingError(fieldErrors) == null;
  // Only surface a field's error once the user has interacted with it.
  const visibleErrors = visibleBillingErrors(fieldErrors, touched);
  const markTouched = (field: string) => setTouched((current) => new Set(current).add(field));

  // Stripe can't report completeness in test mode (plain inputs), so treat the card as complete then.
  const cardComplete = IS_STRIPE_TEST_MODE || isCardComplete;
  const submitDisabled =
    isSubmitDisabled({
      canEdit,
      masqReadOnly,
      isVerificationRejected,
      hasRequiredFields,
      isCardComplete: cardComplete,
      isSubmitting,
    }) ||
    // Block "Pay now" while a coupon apply is in flight so a pending discount (the draft subscription)
    // can't be bypassed by submitting the full-price plan. `runSubmit` also guards on `submitDisabled`,
    // covering Enter-key submits.
    checkout?.isPriceLoading === true ||
    // Same reason for the tax-rate lookup: the shown total belongs to the previous address until it
    // settles.
    checkout?.isTaxRateLoading === true;
  const tooltip = submitTooltipMessage({
    isVerificationRejected,
    verificationRejectedMessage: VERIFICATION_REJECTED_TOOLTIP,
    firstFieldError: firstBillingError(fieldErrors),
    isCardComplete: cardComplete,
    isTaxRateLoading: checkout?.isTaxRateLoading === true,
  });

  const updateAddressField = (field: keyof CreditCardFormAddress, value: string) => {
    const next = applyAddressChange(address, field, value);
    setAddress(next);
    // Let the checkout caller refresh the tax rate from the new address. The field is passed on so the
    // caller can ignore changes that cannot move the rate.
    checkout?.onBillingAddressChange?.(next, field);
  };

  const submitCreditCard = useCreditCardFormSubmit();

  // Opens the address-correction modal and resolves to the address to use, or null if dismissed.
  const resolveAddressCorrection = async (
    input: CreditCardFormAddress,
    corrected: CreditCardFormAddress,
  ): Promise<CreditCardFormAddress | null> => {
    const modalId = modals.open(UpdateAddressModal, { inputAddress: input, correctedAddress: corrected });
    const result = await modals.waitForClose(modalId);
    return result.reason === 'confirm' && result.data != null ? result.data : null;
  };

  // Validates the address first; on a suggested correction it opens the confirmation modal and waits
  // for the user to pick an address, then submits. Dismissing the modal aborts with a prompt.
  const runSubmit = async () => {
    if (submitDisabled) return;
    setIsSubmitting(true);
    try {
      let submitAddress = address;
      const validation = await validateBillingAddress(account.id, submitAddress);
      if (validation?.correctedAddress != null) {
        const chosen = await resolveAddressCorrection(submitAddress, validation.correctedAddress);
        if (chosen == null) {
          snackbars.create({
            id: 'billing.checkout.has-corrected-address',
            variant: 'critical',
            message: 'We found a possible correction for your address. Choose which one to use.',
          });
          return;
        }
        submitAddress = chosen;
        setAddress(chosen);
      }

      await submitCreditCard({
        name,
        address: submitAddress,
        isOrdwayCustomer,
        invoiceEmail,
        stripe,
        cardNumberElement: elements?.getElement(CardNumberElement) ?? null,
        updateAccountAddress: (next) => persistAccountAddress(updateAccountAddress, next),
        onSubmit,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form autoLoading={false} onSubmit={() => void runSubmit()}>
      <CheckoutCouponBanner checkout={checkout} />
      <Stack gap="150">
        <Heading level="2" size="sm">
          Credit card
        </Heading>
        <CardFields onChange={setIsCardComplete} />
        {/* `fs-mask`: the cardholder name is PII — keep it out of FullStory. */}
        <TextField
          data-test-cc-form-name-input
          className="fs-mask"
          label="Full name"
          value={name}
          error={visibleErrors.name}
          onInput={(event) => setName(readInputValue(event))}
          onBlur={() => markTouched('name')}
        />
      </Stack>
      <Stack gap="150" mt="300">
        <Heading level="2" size="sm">
          Billing address
        </Heading>
        <BillingAddressFields
          address={address}
          canEdit={canEdit}
          errors={visibleErrors}
          onChange={updateAddressField}
          onBlur={markTouched}
        />
        <CheckoutCouponSection
          checkout={checkout}
          address={address}
          canApply={canEdit && !masqReadOnly && !isVerificationRejected}
        />
      </Stack>
      <FormFooter
        checkout={checkout}
        canEdit={canEdit}
        secondaryAction={secondaryAction}
        submitButton={
          <SubmitButton
            isDisabled={submitDisabled}
            isLoading={isSubmitting}
            label={submitButtonText}
            tooltip={tooltip}
          />
        }
      />
    </Form>
  );
}

type SubmitButtonProps = {
  isDisabled: boolean;
  isLoading: boolean;
  label: string;
  tooltip: string;
};

function SubmitButton({ isDisabled, isLoading, label, tooltip }: SubmitButtonProps) {
  // Use the custom render-prop tooltip so it attaches to the button via a ref rather than wrapping
  // it in its own trigger button (which nests a <button> inside a <button>). `isDisabled` hides the
  // tooltip when there's no message.
  return (
    <Tooltip content={tooltip} isDisabled={tooltip === ''} isCustom={true}>
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          data-test-cc-form-submit
          variant="primary"
          type="submit"
          isDisabled={isDisabled}
          isLoading={isLoading}
        >
          {label}
        </Button>
      )}
    </Tooltip>
  );
}
